import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, CheckCircle, FileText, Scale, BookOpen,
  Award, Clock, Users, AlertTriangle, Download, ChevronDown, ChevronUp
} from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';

const CompliancePage = () => {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    criminalCode: true,
    culpability: false,
    seizureEntry: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1e3a5f] text-white py-4 px-6 shadow-lg sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <Badge className="w-5 h-5" />
            <span className="font-semibold">POST Compliance & CE Curriculum</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-6">
        {/* Title Section */}
        <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5a8f] rounded-lg p-8 text-white mb-8 shadow-lg">
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-bold">POST Compliance Mapping</h1>
              <p className="text-blue-200">& Continuing Education Curriculum</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm mt-4">
            <span className="bg-white/20 px-3 py-1 rounded">C.R.S. § 10-2-415.5</span>
            <span className="bg-white/20 px-3 py-1 rounded">C.R.S. § 24-31-303(1)(h)</span>
            <span className="bg-white/20 px-3 py-1 rounded">C.R.S. § 10-2-301</span>
            <span className="bg-white/20 px-3 py-1 rounded">POST E-212 Program</span>
          </div>
        </div>

        {/* Purpose Statement */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <p className="text-gray-700 leading-relaxed">
            This page maps course content to the statutory and regulatory standards that govern bail 
            enforcement training in Colorado. It allows auditors, bondsmen, and licensing officials to 
            verify compliance without reading the entire course material.
          </p>
        </div>

        {/* PART I - Statutory Framework */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            PART I — THE STATUTORY FRAMEWORK
          </h2>

          <div className="space-y-6">
            {/* Statute 1 */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-bold text-lg">C.R.S. § 10-2-415.5 — The Bail Recovery Training Requirement</h3>
              <p className="text-gray-500 text-xs mb-2">(Formerly C.R.S. § 12-7-105.5 — renumbered under HB 12-1266)</p>
              <p className="text-gray-700 mt-2">
                Before hiring any individual for bail recovery services, a licensed bail bonding agent must 
                obtain a certificate showing that the individual has received training in bail fugitive 
                apprehension from a private bail recovery program or an accredited institution of higher education.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                <p className="text-sm text-blue-800">
                  <strong>What this means:</strong> The statute specifies a 16-hour minimum covering five POST-required 
                  topic areas. This course provides <strong> 24 credit hours</strong> — exceeding the requirement by 1.5x. 
                  The compliance certificate issued to graduates documents that the 16-hour statutory minimum is satisfied.
                </p>
              </div>
            </div>

            {/* Statute 2 */}
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-bold text-lg">C.R.S. § 24-31-303(1)(h) — POST Board Standards for Bail Recovery</h3>
              <p className="text-gray-700 mt-2">
                This section directs the POST Board to establish minimum curriculum standards for bail recovery 
                training programs. Those standards are codified as the <strong>Bail Recovery Training Program (E-212)</strong> in 
                the POST Manual.
              </p>
              <div className="bg-green-50 border border-green-200 rounded p-3 mt-3">
                <h4 className="font-semibold text-green-800 mb-2">THE FIVE POST-REQUIRED TOPIC AREAS (E-212) — 16 Hours</h4>
                <ol className="list-decimal list-inside text-sm text-green-800 space-y-1">
                  <li>Introduction to Bail Recovery — 3 hours</li>
                  <li>The Colorado Criminal Code — applicable offenses, elements, and classifications — 3 hours</li>
                  <li>Principles of Criminal Culpability — C.R.S. § 18-1-501, 502, and 503 — 3 hours</li>
                  <li>Firearms and Weapons — lawful carry, prohibited weapons, use of force — 3 hours</li>
                  <li>Laws Regarding Seizure and Entry — Fourth Amendment application, C.R.S. § 16-3-301 through 306 — 2 hours (minimum; this course exceeds significantly)</li>
                </ol>
              </div>
            </div>

            {/* Statute 3 */}
            <div className="border-l-4 border-amber-500 pl-4">
              <h3 className="font-bold text-lg">C.R.S. § 10-2-301 — Continuing Education Requirement</h3>
              <p className="text-gray-700 mt-2">
                The Commissioner of Insurance requires bail bonding agents to complete <strong>24 hours of 
                continuing education every two years</strong> as a condition of license renewal.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-3">
                <p className="text-sm text-amber-800">
                  The 24-hour biennial CE structure in Part III is designed to satisfy this requirement completely, 
                  with content organized to ensure POST-required topics are revisited every renewal cycle.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* PART II - POST E-212 Compliance Map */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-6 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            PART II — POST E-212 COMPLIANCE MAP
          </h2>

          {/* Topic Area 1 */}
          <div className="mb-6">
            <button 
              onClick={() => toggleSection('criminalCode')}
              className="w-full flex items-center justify-between bg-gray-100 p-4 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <h3 className="font-bold text-lg">Required Topic Area 1: The Colorado Criminal Code</h3>
              {expandedSections.criminalCode ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.criminalCode && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-[#1e3a5f] text-white">
                      <th className="p-3 text-left">POST Requirement</th>
                      <th className="p-3 text-left">Course Coverage</th>
                      <th className="p-3 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr className="hover:bg-gray-50">
                      <td className="p-3">Colorado Criminal Code — applicable offenses</td>
                      <td className="p-3 font-mono text-xs">Ch. 1, § 1.1-1.2; Ch. 7, § 7.1</td>
                      <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="p-3">Offense classifications: felony, misdemeanor, petty</td>
                      <td className="p-3 font-mono text-xs">Ch. 1, § 1.2; Ch. 2, § 2.4; Ch. 5, § 5.1</td>
                      <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="p-3">Crimes against persons relevant to bail enforcement</td>
                      <td className="p-3 font-mono text-xs">Ch. 2, § 2.4; Ch. 5, § 5.1-5.2</td>
                      <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="p-3">Obstruction of public justice (C.R.S. § 18-8-102–116)</td>
                      <td className="p-3 font-mono text-xs">Ch. 3, § 3.5, 3.9; Ch. 7, § 7.1</td>
                      <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="p-3">Escape and custody offenses (C.R.S. § 18-8-201–212)</td>
                      <td className="p-3 font-mono text-xs">Ch. 4, § 4.11; Ch. 7, § 7.1-7.3</td>
                      <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="p-3">Impersonation of law enforcement (C.R.S. § 18-8-112)</td>
                      <td className="p-3 font-mono text-xs">Ch. 3, § 3.3</td>
                      <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="p-3">Weapons offenses (C.R.S. § 18-12-101 et seq.)</td>
                      <td className="p-3 font-mono text-xs">Ch. 9, § 9.1-9.3</td>
                      <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="p-3">False reporting / tampering (C.R.S. § 18-8-503, 708)</td>
                      <td className="p-3 font-mono text-xs">Ch. 3, § 3.5</td>
                      <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="p-3">Kidnapping (C.R.S. § 18-3-301–306; 18 U.S.C. § 1201)</td>
                      <td className="p-3 font-mono text-xs">Ch. 3, § 3.13; Ch. 8, § 8.3</td>
                      <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Topic Area 2 */}
          <div className="mb-6">
            <button 
              onClick={() => toggleSection('culpability')}
              className="w-full flex items-center justify-between bg-gray-100 p-4 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <h3 className="font-bold text-lg">Required Topic Area 2: Principles of Criminal Culpability</h3>
              {expandedSections.culpability ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.culpability && (
              <div className="mt-4">
                <p className="text-gray-600 mb-4 text-sm">
                  C.R.S. § 18-1-501, 502, and 503 establish the mental states (mens rea) required for criminal 
                  liability in Colorado. Agents must understand these because they directly govern the agent's 
                  own criminal exposure for every field decision.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1e3a5f] text-white">
                        <th className="p-3 text-left">POST Requirement</th>
                        <th className="p-3 text-left">Course Coverage</th>
                        <th className="p-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">C.R.S. § 18-1-501 definitions of culpable mental states</td>
                        <td className="p-3 font-mono text-xs">Ch. 3, § 3.1; Ch. 5, § 5.1</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">C.R.S. § 18-1-502 — culpability required for offenses</td>
                        <td className="p-3 font-mono text-xs">Ch. 3, § 3.4, 3.5</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">C.R.S. § 18-1-503 — culpability as element of offense</td>
                        <td className="p-3 font-mono text-xs">Ch. 5, § 5.1-5.2</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Intentionally / Knowingly / Recklessly / Criminal Negligence</td>
                        <td className="p-3 font-mono text-xs">Ch. 3, § 3.4; Ch. 5, § 5.1–5.4</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Strict liability distinguished from culpability-based offenses</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.4; Ch. 5, § 5.1</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Complicity and accountability (C.R.S. § 18-1-601–605)</td>
                        <td className="p-3 font-mono text-xs">Ch. 3, § 3.13</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Justification and exemption from criminal responsibility</td>
                        <td className="p-3 font-mono text-xs">Ch. 5, § 5.1-5.4</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Topic Area 3 */}
          <div className="mb-6">
            <button 
              onClick={() => toggleSection('seizureEntry')}
              className="w-full flex items-center justify-between bg-gray-100 p-4 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <h3 className="font-bold text-lg">Required Topic Area 3: Laws Regarding Seizure and Entry</h3>
              {expandedSections.seizureEntry ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            {expandedSections.seizureEntry && (
              <div className="mt-4">
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                  <p className="text-sm text-red-800">
                    <strong>Critical Area:</strong> This is where Colorado bail enforcement law is most unsettled and 
                    most dangerous for untrained agents. This course addresses this topic more thoroughly than any 
                    other, given its practical importance and legal complexity.
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-[#1e3a5f] text-white">
                        <th className="p-3 text-left">POST Requirement</th>
                        <th className="p-3 text-left">Course Coverage</th>
                        <th className="p-3 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Fourth Amendment — scope, applicability, private actor analysis</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.1</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Reasonable expectation of privacy — home vs. public space</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.1–2.2</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Consent searches — validity, voluntariness, coercion</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.6</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">C.R.S. § 16-3-301-306 search and seizure statutes</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.1-2.4; Ch. 7, § 7.1</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Third-party residences — entry authority and prohibition</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.2-2.8</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Steagald doctrine — bond is not a search warrant</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.3</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Castle Doctrine (C.R.S. § 18-1-704.5)</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.5</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Criminal trespass (C.R.S. § 18-4-502–503)</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.4</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">First-degree burglary (C.R.S. § 18-4-202)</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.4</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Oram v. People (2011) — Colorado Supreme Court limits</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.1, 2.3</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">42 U.S.C. § 1983 — civil rights liability</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.7</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="p-3">Vehicle stops and public contact</td>
                        <td className="p-3 font-mono text-xs">Ch. 2, § 2.9; Ch. 4, § 4.10</td>
                        <td className="p-3 text-center"><span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold">COVERED</span></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Compliance Summary */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mt-6">
            <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Compliance Summary
            </h3>
            <ul className="space-y-2 text-green-800">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>All five POST E-212 required topic areas are fully covered in this course.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Introduction to Bail Recovery (3 hr minimum) — covered in Ch. 1 with extensive treatment of bond types, forfeiture timelines, and surety authority.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Colorado Criminal Code coverage exceeds POST requirements — addressed throughout Chapters 1, 2, 3, 5, 7, and 9.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Principles of Criminal Culpability (C.R.S. § 18-1-501, 502, 503) are addressed in context of bail enforcement decisions.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Firearms and Weapons (3 hr minimum) — covered in Ch. 5 (force continuum) and Ch. 9 (weapons & defensive tactics).</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>Seizure and entry law is treated more extensively than any other topic — where agents face the greatest exposure.</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-1 flex-shrink-0" />
                <span><strong>The 16-hour statutory minimum under C.R.S. § 10-2-415.5 is fully satisfied within Chapters 1 through 5 of this course.</strong></span>
              </li>
            </ul>
          </div>
        </div>

        {/* 16-Hour Certification Block */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-6 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            The 16-Hour Certification Block
          </h2>
          <p className="text-gray-600 mb-4">
            For documentation purposes, the following course hours satisfy the 16-hour statutory minimum 
            under C.R.S. § 10-2-415.5 and POST E-212:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-[#1e3a5f] text-white">
                  <th className="p-3 text-left">Content Area</th>
                  <th className="p-3 text-left">Chapter / Sections</th>
                  <th className="p-3 text-right">Hours</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr className="hover:bg-gray-50">
                  <td className="p-3">Introduction to Bail Recovery — bail bond system, authority, forfeiture, DOI</td>
                  <td className="p-3 font-mono text-xs">Ch. 1, § 1.1-1.5</td>
                  <td className="p-3 text-right font-bold">3.0 hr</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="p-3">Colorado Criminal Code — offenses, classifications, obstruction, trespass</td>
                  <td className="p-3 font-mono text-xs">Ch. 1 § 1.9; Ch. 2 § 2.4; Ch. 3 § 3.5</td>
                  <td className="p-3 text-right font-bold">3.0 hr</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="p-3">Principles of Criminal Culpability (§ 18-1-501, 502, 503)</td>
                  <td className="p-3 font-mono text-xs">Ch. 3, § 3.1-3.5; Ch. 5, § 5.1</td>
                  <td className="p-3 text-right font-bold">3.0 hr</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="p-3">Firearms and Weapons — lawful carry, prohibited weapons, force continuum</td>
                  <td className="p-3 font-mono text-xs">Ch. 5, § 5.1-5.4; Ch. 9, § 9.1-9.3</td>
                  <td className="p-3 text-right font-bold">3.0 hr</td>
                </tr>
                <tr className="hover:bg-gray-50">
                  <td className="p-3">Laws Regarding Seizure and Entry — Fourth Amendment, consent, third-party residences</td>
                  <td className="p-3 font-mono text-xs">Ch. 2, § 2.1-2.8</td>
                  <td className="p-3 text-right font-bold">4.0 hr</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="bg-green-100">
                  <td colSpan="2" className="p-3 font-bold text-green-800">STATUTORY MINIMUM SATISFIED (C.R.S. § 10-2-415.5)</td>
                  <td className="p-3 text-right font-bold text-green-800">16.0 hr</td>
                </tr>
                <tr className="bg-blue-100">
                  <td colSpan="2" className="p-3 font-bold text-blue-800">FULL COURSE TOTAL (including case studies, practice exams, final exam)</td>
                  <td className="p-3 text-right font-bold text-blue-800">24 hr</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* PART IV - Certificate Requirements */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <h2 className="text-xl font-bold text-[#1e3a5f] mb-6 flex items-center gap-2">
            <Award className="w-5 h-5" />
            PART IV — CERTIFICATE AND DOCUMENTATION REQUIREMENTS
          </h2>

          {/* Initial Certification */}
          <div className="mb-6">
            <h3 className="font-bold text-lg mb-3">Initial Certification Certificate</h3>
            <p className="text-gray-600 mb-3">
              Upon completion of the full course, the graduate receives a Certificate of Completion in 
              Bail Fugitive Apprehension Training. The certificate must reflect:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>Full legal name of the graduate</li>
              <li>Date of completion</li>
              <li>Name of the training program and provider</li>
              <li>Statement that the training complies with POST standards under C.R.S. § 24-31-303(1)(h)</li>
              <li>Total instructional hours completed</li>
              <li>Signature of the course instructor or program director</li>
            </ul>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 mt-4">
              <p className="text-sm text-amber-800">
                <strong>Retention:</strong> The graduate must retain a copy for a minimum of <strong>5 years</strong>. 
                The hiring bondsman must also retain a copy as a condition of compliance with C.R.S. § 10-2-415.5.
              </p>
            </div>
          </div>

          {/* Documentation Chain */}
          <div>
            <h3 className="font-bold text-lg mb-3">The Documentation Chain: What Each Party Must Hold</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#1e3a5f] text-white">
                    <th className="p-3 text-left">Party</th>
                    <th className="p-3 text-left">Required Document</th>
                    <th className="p-3 text-left">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr className="hover:bg-gray-50">
                    <td className="p-3 font-semibold">Agent</td>
                    <td className="p-3">Certificate of Completion — initial certification</td>
                    <td className="p-3">5 years minimum</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-3 font-semibold">Agent</td>
                    <td className="p-3">CE Credit Certificate — each biennial renewal cycle</td>
                    <td className="p-3">Duration of license + 2 years</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-3 font-semibold">Bondsman</td>
                    <td className="p-3">Copy of agent's initial certification certificate</td>
                    <td className="p-3">Duration of employment + 5 years</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-3 font-semibold">Bondsman</td>
                    <td className="p-3">Signed statement from agent attesting to accuracy</td>
                    <td className="p-3">Duration of employment + 5 years</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-3 font-semibold">Bondsman</td>
                    <td className="p-3">CBI fingerprint confirmation for each agent</td>
                    <td className="p-3">Duration of employment + 5 years</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-3 font-semibold">Training Provider</td>
                    <td className="p-3">Records of all completions — initial and CE</td>
                    <td className="p-3">5 years per participant</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* DOI Audit Note */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-bold text-red-800">NOTE ON DOI AUDIT READINESS</h4>
                <p className="text-sm text-red-700 mt-1">
                  The Colorado Division of Insurance has authority to revoke a 
                  bondsman's license for hiring an agent in violation of C.R.S. § 10-2-415.5. In a DOI audit or 
                  complaint investigation, the first thing an investigator will request is the documentation 
                  chain above. <strong>Maintain these records.</strong> They are not administrative formalities — 
                  they are the paper trail that demonstrates compliance.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Course */}
        <div className="flex justify-center mt-8">
          <Button onClick={() => navigate('/')} className="bg-[#1e3a5f]">
            <BookOpen className="w-4 h-4 mr-2" />
            Return to Course
          </Button>
        </div>
      </main>
    </div>
  );
};

export default CompliancePage;
